/**
 * Running out of room.
 *
 * `probeIndexedDbAvailability` answers a different question: whether IndexedDB
 * exists at all, which is what private browsing and disabled storage look
 * like. This module covers the case where the database opens perfectly and
 * then a write fails because the origin is full -- a failure that arrives
 * later, on an ordinary interaction, and which every browser reports slightly
 * differently.
 *
 * Nothing here throws. A cache that cannot write is a degraded experience, not
 * a broken one: the app still works online, and the reader is told what
 * stopped rather than left with a page that silently forgets things.
 */

/** Above this share of the origin's quota, caching stops adding to the problem. */
export const QUOTA_PRESSURE_RATIO = 0.9;

export interface StorageEstimate {
  usage: number;
  quota: number;
  /** 0 to 1. `null` when the browser reports a quota of zero or omits one. */
  ratio: number | null;
}

/**
 * Detects a quota failure across the several shapes browsers use for it.
 *
 * Firefox and Safari have historically reported this with different names and
 * legacy codes, and Dexie wraps the underlying error, so a check for one exact
 * name would pass tests and miss the real thing.
 */
export function isQuotaExceeded(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;

  const candidates: unknown[] = [error];
  // Dexie surfaces the original as `inner`; DOMException chains use `cause`.
  for (const key of ["inner", "cause"]) {
    if (key in error) candidates.push((error as Record<string, unknown>)[key]);
  }

  return candidates.some((candidate) => {
    if (typeof candidate !== "object" || candidate === null) return false;
    const record = candidate as { name?: unknown; code?: unknown; message?: unknown };
    if (record.name === "QuotaExceededError" || record.name === "NS_ERROR_DOM_QUOTA_REACHED") {
      return true;
    }
    // Legacy DOMException code, still emitted by some engines.
    if (record.code === 22) return true;
    return typeof record.message === "string" && /quota/iu.test(record.message);
  });
}

/*
 * Partial rather than the DOM lib's `StorageManager`, which declares both
 * methods as always present. That is not true of older WebViews and Safari
 * versions, and typing them as guaranteed makes the runtime guards below read
 * as dead code to the linter while leaving the real gap unhandled.
 */
export type PartialStorageManager = Partial<Pick<StorageManager, "estimate" | "persist">>;

/**
 * The browser's storage manager, typed as the optional thing it actually is.
 *
 * `globalThis.navigator` is declared non-nullable, so reading it directly hands
 * back a type that can never be absent — in a service worker, an older engine,
 * or a test environment, it can.
 */
function browserStorage(): PartialStorageManager | undefined {
  return (globalThis.navigator as Navigator | undefined)?.storage;
}

export async function readStorageEstimate(
  storage: PartialStorageManager | undefined = browserStorage(),
): Promise<StorageEstimate | null> {
  // Not universally implemented, and a missing estimate is not a failure.
  if (typeof storage?.estimate !== "function") return null;
  try {
    const { quota, usage } = await storage.estimate();
    if (usage === undefined || quota === undefined) return null;
    return { usage, quota, ratio: quota > 0 ? usage / quota : null };
  } catch {
    return null;
  }
}

/** True when the origin is close enough to full that more caching is unwise. */
export function isUnderQuotaPressure(estimate: StorageEstimate | null): boolean {
  return estimate?.ratio !== null && estimate !== null && estimate.ratio >= QUOTA_PRESSURE_RATIO;
}

/**
 * Asks the browser to exempt this origin from eviction.
 *
 * Worth asking once for an offline-first app: without it a browser under
 * pressure may clear the cache between sessions, which reads to the user as
 * data loss rather than as a cache miss. Browsers may grant it silently, deny
 * it, or prompt; all three are fine and none are errors.
 */
export async function requestPersistentStorage(
  storage: PartialStorageManager | undefined = browserStorage(),
): Promise<boolean> {
  if (typeof storage?.persist !== "function") return false;
  try {
    return await storage.persist();
  } catch {
    return false;
  }
}

/**
 * Runs a cache write, absorbing the case where there is no room for it.
 *
 * Returns whether the write happened, so a caller can report degradation. Any
 * other error is rethrown: swallowing everything would turn a schema bug into
 * a silent cache that never populates and never says why.
 */
export async function writeWithQuotaGuard(write: () => Promise<unknown>): Promise<boolean> {
  try {
    await write();
    return true;
  } catch (error) {
    if (isQuotaExceeded(error)) return false;
    throw error;
  }
}
